import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import NoDataScreen from "./NoDataScreen";
import Button from "components/common/Button";
import DependentsModal from "./DependentsModal";
import { convertDate } from "components/Screens/CommonUtils";
import { AlertType } from "components/common/Snackbar";
import SectionLoader from "components/common/SectionLoader";

import { showSnackbar } from "store/actions/Utility";
import { getDependentsDetails } from "services/Dependents";

import { ModeEditOutlined } from "@mui/icons-material";

import "./Dependents.scss";

function FieldRow({ label, value }) {
  return (
    <div className="flex justify-between items-center">
      <h5 className="m-0 heading-h5-regular">{label}</h5>
      <p className="contact-value para-body-m-semibold">{value}</p>
    </div>
  );
}

function FieldsList({ fields }) {
  return (
    <div>
      <div className="flex flex-col gap-1">
        {fields.map((field) => (
          <FieldRow key={field.label} label={field.label} value={field.value} />
        ))}
      </div>
    </div>
  );
}

function DependentSection({ title, fields, onEdit, gap, showDivider = false }) {
  return (
    <>
      <div className={`flex justify-between items-center ${gap}`}>
        <h4 className="heading-h4-semibold m-0 flex items-center">{title}</h4>
        <Button variant="outlined" size="small" label="Edit" startIcon={<ModeEditOutlined />} onClick={onEdit} />
      </div>
      <FieldsList fields={fields} />
      {showDivider && <div className="dependent-hr" />}
    </>
  );
}

function Dependents() {
  const { id: employeeId } = useParams();
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [initialTab, setInitialTab] = useState(0);
  const [dependentsData, setDependentsData] = useState(null);
  const [isLoadingDependentsData, setIsLoadingDependentsData] = useState(false);

  const { data: dependants = [] } = dependentsData || {};

  const groupedDependents = dependants.reduce((acc, dependent) => {
    const { relationship } = dependent;
    if (!acc[relationship]) {
      acc[relationship] = [];
    }
    acc[relationship].push(dependent);
    return acc;
  }, {});

  const getInitialFormData = () => {
    const parents = groupedDependents?.parent || [];
    const spouses = groupedDependents?.spouse || [];
    const children = groupedDependents?.child || [];

    const father = parents.find((p) => p.gender === "Male");
    const mother = parents.find((p) => p.gender === "Female");
    const spouse = spouses[0];

    return {
      fatherName: father?.name || "",
      fatherNationalID: father?.nationalId || "",
      motherName: mother?.name || "",
      motherNationalID: mother?.nationalId || "",
      spouseName: spouse?.name || "",
      spouseNationalID: spouse?.nationalId || "",
      spouseDateOfBirth: spouse?.dateOfBirth || "",
      children:
        children.length > 0
          ? children.map((child) => ({
              name: child.name || "",
              nationalID: child.nationalId || "",
              dateOfBirth: child.dateOfBirth || "",
            }))
          : [{ name: "", nationalID: "", dateOfBirth: "" }],
    };
  };

  const handleEdit = (tabIndex = 0) => {
    setInitialTab(tabIndex);
    setIsModalOpen(true);
  };

  const createParentsFields = (parents) => {
    const fields = [];
    const father = parents.find((p) => p.gender === "Male");
    const mother = parents.find((p) => p.gender === "Female");

    if (father) {
      fields.push({ label: "Father name", value: father.name || "N/A" });
      fields.push({ label: "Father National ID", value: father.nationalId || "N/A" });
    }
    if (mother) {
      fields.push({ label: "Mother name", value: mother.name || "N/A" });
      fields.push({ label: "Mother National ID", value: mother.nationalId || "N/A" });
    }
    return fields;
  };

  const createSpouseFields = (spouse) => {
    return [
      { label: "Name", value: spouse.name || "N/A" },
      { label: "National ID", value: spouse.nationalId || "N/A" },
      { label: "Date of birth", value: convertDate(spouse.dateOfBirth) || "N/A" },
    ];
  };

  const createChildFields = (child) => {
    return [
      { label: "Name", value: child.name || "N/A" },
      { label: "National ID", value: child.nationalId || "N/A" },
      { label: "Date of birth", value: convertDate(child.dateOfBirth) || "N/A" },
    ];
  };

  const parents = groupedDependents?.parent || [];
  const spouses = groupedDependents?.spouse || [];
  const children = groupedDependents?.child || [];

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const fetchDependentsData = async () => {
    setIsLoadingDependentsData(true);
    try {
      if (employeeId) {
        const dependentsResponse = await getDependentsDetails(employeeId);
        setDependentsData(dependentsResponse);
      }
    } catch (error) {
      dispatch(showSnackbar({ type: AlertType.error, message: "Failed to fetch dependents details", dispatch }));
    } finally {
      setIsLoadingDependentsData(false);
    }
  };

  const handleSaveSuccess = () => {
    fetchDependentsData();
  };

  useEffect(() => {
    fetchDependentsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  if (dependentsData?.data?.length === 0) {
    return (
      <>
        <NoDataScreen onAddDependents={() => setIsModalOpen(true)} />
        <DependentsModal
          open={isModalOpen}
          onClose={handleClose}
          employeeId={employeeId}
          initialData={null}
          onSaveSuccess={handleSaveSuccess}
        />
      </>
    );
  }

  return (
    <div className="dependents-container">
      {isLoadingDependentsData ? (
        <SectionLoader size={24} />
      ) : (
        <>
          {parents.length > 0 && (
            <DependentSection
              title="Parents"
              gap="mb-1"
              fields={createParentsFields(parents)}
              onEdit={() => handleEdit(0)}
            />
          )}

          {spouses.length > 0 && (
            <>
              {spouses.map((spouse) => (
                <DependentSection
                  key={spouse.id}
                  title="Spouse"
                  gap="my-1"
                  fields={createSpouseFields(spouse)}
                  onEdit={() => handleEdit(1)}
                />
              ))}
            </>
          )}

          {children.length > 0 && (
            <>
              {children.map((child, index) => (
                <React.Fragment key={child.id}>
                  {index === 0 ? (
                    <DependentSection
                      title="Children"
                      gap="my-1"
                      fields={createChildFields(child)}
                      onEdit={() => handleEdit(2)}
                      showDivider={children.length > 1}
                    />
                  ) : (
                    <>
                      <FieldsList fields={createChildFields(child)} />
                      {index < children.length - 1 && <div className="dependent-hr" />}
                    </>
                  )}
                </React.Fragment>
              ))}
            </>
          )}

          <DependentsModal
            open={isModalOpen}
            onClose={handleClose}
            employeeId={employeeId}
            initialData={getInitialFormData()}
            initialTab={initialTab}
            onSaveSuccess={handleSaveSuccess}
          />
        </>
      )}
    </div>
  );
}

export default Dependents;
